import { useState } from "react";
import { useNavigate } from "react-router";
import { Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  GOAL_DATA_SOURCES,
  GOAL_DATA_SOURCE_LABEL,
  GOAL_FREQUENCIES,
  GOAL_FREQUENCY_LABEL,
  type GoalDataSource,
  type GoalFrequency,
} from "@colab/shared";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCreateGoal } from "./use-create-goal";

// SPA port of /app/admin/goals/_components/NewGoalDialog.tsx.

const DEFAULT_DATE_PROPERTY: Record<GoalDataSource, string> = {
  deal: "created_at",
  submission: "created_at",
  account: "created_at",
  journey_progress: "completed_at",
};

export function NewGoalDialog({
  variant = "default",
}: {
  variant?: "default" | "empty";
}) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const mutation = useCreateGoal();

  const [title, setTitle] = useState("");
  const [dataSource, setDataSource] = useState<GoalDataSource>("deal");
  const [frequency, setFrequency] = useState<GoalFrequency>("quarterly");

  function reset() {
    setTitle("");
    setDataSource("deal");
    setFrequency("quarterly");
  }

  function onSubmit() {
    mutation.mutate(
      {
        partner_id: null,
        title: title.trim(),
        description: null,
        aggregation_kind: "count",
        aggregation_property: null,
        data_source: dataSource,
        date_property: DEFAULT_DATE_PROPERTY[dataSource],
        attribution: "all",
        filter: { kind: "group", version: 1, combinator: "AND", children: [] },
        frequency,
      },
      {
        onSuccess: (res) => {
          toast.success("Goal created");
          reset();
          setOpen(false);
          navigate(`/admin/goals/${res.id}`);
        },
      },
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        setOpen(o);
      }}
    >
      <DialogTrigger asChild>
        {variant === "empty" ? (
          <Button>
            <Plus className="mr-2 h-4 w-4" />
            Create your first goal
          </Button>
        ) : (
          <Button size="sm">
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            New goal
          </Button>
        )}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">
            New goal
          </DialogTitle>
          <DialogDescription>
            Pick a title, source and frequency. You can refine the aggregation,
            filter and per-partner targets in the editor.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="ng-title">Title</Label>
            <Input
              id="ng-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Close 10 deals this quarter"
              required
              maxLength={200}
              autoFocus
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="ng-source">Data source</Label>
              <Select
                value={dataSource}
                onValueChange={(v) => setDataSource(v as GoalDataSource)}
              >
                <SelectTrigger id="ng-source">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {GOAL_DATA_SOURCES.map((d) => (
                    <SelectItem key={d} value={d}>
                      {GOAL_DATA_SOURCE_LABEL[d]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ng-freq">Frequency</Label>
              <Select
                value={frequency}
                onValueChange={(v) => setFrequency(v as GoalFrequency)}
              >
                <SelectTrigger id="ng-freq">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {GOAL_FREQUENCIES.map((f) => (
                    <SelectItem key={f} value={f}>
                      {GOAL_FREQUENCY_LABEL[f]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => setOpen(false)}
            disabled={mutation.isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={onSubmit}
            disabled={mutation.isPending || !title.trim()}
          >
            {mutation.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Create goal
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
